import Hls, {
  FragChangedData,
  Fragment,
  ManifestLoadedData,
  FragBufferedData,
  Level,
  ErrorData,
} from 'hls.js';
import { BehaviorSubject, Observable, Subject, Subscription, interval } from 'rxjs';
import { debounceTime, take, takeUntil } from 'rxjs/operators';

import { Platform } from '@angular/cdk/platform';

import { FsDataDogRumService } from '@lib/data-dog-rum';
import { SnPlayerState } from '@lib/player/common';

import { StreamContext } from '../services';
import { SnVideoPlayerType } from '../video-player.enums';
import { SnVideoPlayerReference } from '../video-player.reference';
import { PLAYERS_REFERENCE } from './players.reference.constants';
import { emitStreamError } from './stream-error.utils';

class RunningMean {
  private n: number = 0;
  private mean: number = 0;

  constructor() {
    this.n = 0;
    this.mean = 0;
  }

  addValue(value: number): void {
    this.n++;
    this.mean += (value - this.mean) / this.n;
  }

  getMean(): number {
    return this.mean;
  }

  hasValues(): boolean {
    return this.n > 0;
  }

  clear(): void {
    this.n = 0;
    this.mean = 0;
  }
}

export class SnHlsPlayerReference extends SnVideoPlayerReference {
  get isPlaying(): boolean {
    return !this.videoElement.paused;
  }

  public hls: Hls;
  public isBufferError = false;
  public isBufferStalled: boolean = false;
  public isStreamRecovering: boolean = false;
  private _lastPosition: number = 0;
  private _src: string;
  private _wasPaused: boolean = false;
  private _currentFrag: Fragment;
  private _destroy$ = new Subject();
  private _connected: boolean;
  private _playStartTime: number;
  private _resumePlayStartTime: number;
  private _stallStartTime: number;
  private _stallCount: number = 0;
  private _downloadAsFractionOfSegLength: RunningMean = new RunningMean();
  private _periodicMetricsSubscription: Subscription;
  private _periodicMetricsIntervalMs: number = 30_000;
  private _seekStartTime: number;
  private _isSeeking: boolean = false;
  private readonly _seekStart$ = new Subject<void>();
  private _seekSubscription: Subscription;
  private _firstFrameLoaded: boolean = false;

  private callback: () => void;
  private readonly _levelLoaded$ = new Subject<void>();
  private readonly _fragLoaded$ = new Subject<void>();
  private readonly _fragLoading$ = new Subject<void>();
  private readonly _fragChanged$ = new Subject<void>();
  private _targetDurationMs: number;

  private _playingHandler: () => void;
  private _pauseHandler: () => void;
  private _seekingHandler: () => void;
  private _seekedHandler: () => void;

  constructor(
    playerElement: HTMLElement,
    videoElement: HTMLVideoElement,
    src: string,
    autoplay: boolean,
    platform: Platform,
    state$: BehaviorSubject<SnPlayerState>,
    currentTime$: BehaviorSubject<number>,
    bufferTime$: BehaviorSubject<number>,
    totalTime$: BehaviorSubject<number>,
    volume$: BehaviorSubject<number>,
    played$: BehaviorSubject<null>,
    loaded$: Subject<boolean>,
    ended$: Subject<void>,
    private readonly _datadogService: FsDataDogRumService,
    callback: () => void,
    private context: StreamContext,
    private errorCallback: () => void,
  ) {
    super(
      SnVideoPlayerType.HLS,
      playerElement,
      platform,
      state$,
      currentTime$,
      bufferTime$,
      totalTime$,
      volume$,
      played$,
      loaded$,
      ended$,
    );
    this._init(videoElement, src, autoplay);
    this.callback = callback;
    currentTime$.pipe(takeUntil(this._destroy$)).subscribe((currentTime) => {
      if (this._currentFrag && this._currentFrag.programDateTime) {
        const fragStart = this._currentFrag.start;
        const fragProgramDate: number = this._currentFrag.programDateTime;
        const diff = currentTime - fragStart;
        this.actualTime = Math.floor(fragProgramDate + diff * 1000);
      }
    });
    this._startPeriodicMetrics();
    this._initSeekDebounce();
    loaded$.pipe(take(1), takeUntil(this._destroy$)).subscribe(() => {
      this._firstFrameLoaded = true;
      this._emitStreamAttemptEnd('success', 'manifest');
    });
  }

  get fragLoaded$(): Observable<void> {
    return this._fragLoaded$.asObservable();
  }

  get fragLoading$(): Observable<void> {
    return this._fragLoading$.asObservable();
  }

  get levelLoaded$(): Observable<void> {
    return this._levelLoaded$.asObservable();
  }

  protected getFragChangedObservable(): Observable<void> {
    return this._fragChanged$.asObservable();
  }

  public play(): void {
    if (this._wasPaused) {
      this._resumePlayStartTime = Date.now();
    }
    this.videoElement.play().catch((error) => {
      const errorString = error?.toString() || JSON.stringify(error);
      const isAbortError = errorString.indexOf('AbortError: The play()') !== -1;
      const errorType = error?.type || 'video_element_play_error';
      const errorDetails = error?.details || errorString;
      if (this._datadogService && this.context.camera_id) {
        emitStreamError({
          datadogService: this._datadogService,
          cameraId: this.context.camera_id,
          protocol: PLAYERS_REFERENCE.PROTOCOL.HLS,
          sourceUi: PLAYERS_REFERENCE.SOURCE_UI.HLS,
          errorType,
          rawError: errorDetails,
          errorCode: error?.code || 'UNKNOWN',
          isLive: this.live,
          isFatal: !isAbortError,
          context: this.context,
        });
      }
      console.error('HLS PLAYER ERROR:', error);
      if (isAbortError) {
        return null;
      }
      return error;
    });
  }

  public pause(): void {
    this.videoElement.pause();
  }

  public destroy(): void {
    super.destroy();

    if (this.videoElement) {
      if (this._playingHandler) {
        this.videoElement.removeEventListener('playing', this._playingHandler);
      }
      if (this._pauseHandler) {
        this.videoElement.removeEventListener('pause', this._pauseHandler);
      }
      if (this._seekingHandler) {
        this.videoElement.removeEventListener('seeking', this._seekingHandler);
      }
      if (this._seekedHandler) {
        this.videoElement.removeEventListener('seeked', this._seekedHandler);
      }
    }

    if (this.hls) {
      this.hls.detachMedia();
      this.hls.destroy();
    }
    this._connected = false;
    this._destroy$.next();
    this._destroy$.complete();
    this._fragChanged$.complete();
    this._fragLoaded$.complete();
    this._fragLoading$.complete();
    this._levelLoaded$.complete();
    this._seekStart$.complete();
    this._stopPeriodicMetrics();
    if (this._seekSubscription) {
      this._seekSubscription.unsubscribe();
    }

    if (!this._firstFrameLoaded) {
      this._emitStreamAttemptEnd('canceled', 'manifest');
    }
    if (this._firstFrameLoaded && this._isSeeking) {
      this._emitStreamAttemptEnd('canceled', 'seek');
    }
  }

  private _init(element: HTMLVideoElement, src: string, autoplay: boolean): void {
    this._emitStreamAttemptStart('manifest');
    this._playStartTime = Date.now();
    this.videoElement = element;
    this.videoElement.src = src;
    this._src = src;

    this._playingHandler = () => {
      if (this._wasPaused && this._resumePlayStartTime) {
        const playbackLatency = Date.now() - this._resumePlayStartTime;
        this._emitResumePlaybackLatency(playbackLatency);
        this._wasPaused = false;
        this._resumePlayStartTime = null;
      }
    };
    this.videoElement.addEventListener('playing', this._playingHandler);

    this._pauseHandler = () => {
      this._wasPaused = true;
    };
    this.videoElement.addEventListener('pause', this._pauseHandler);

    this._seekingHandler = () => {
      this._handleSeekStart();
    };
    this.videoElement.addEventListener('seeking', this._seekingHandler);

    this._seekedHandler = () => {
      this._handleSeekEnd();
    };
    this.videoElement.addEventListener('seeked', this._seekedHandler);
    if (Hls.isSupported()) {
      this.hls = new Hls({
        fragLoadingTimeOut: 10000,
        maxFragLookUpTolerance: 0.5,
        fragLoadingMaxRetry: 6,
        manifestLoadingMaxRetry: 3,
        liveSyncDurationCount: 5,
        startFragPrefetch: true,
        initialLiveManifestSize: 0,
        backBufferLength: 300,
        maxBufferLength: 30,
        maxMaxBufferLength: 60,
        levelLoadingMaxRetry: 5,
        levelLoadingMaxRetryTimeout: 10000,
      });
      this.hls.loadSource(src);
      this.hls.attachMedia(this.videoElement);
      this.hls.on(Hls.Events.ERROR, (error: string, data: ErrorData) => {
        if (this._datadogService && this.context.camera_id) {
          const errorCode =
            typeof data.details === 'number' ? data.details : data.details || 'UNKNOWN';
          const errorDetails = JSON.stringify({ error, data });
          emitStreamError({
            datadogService: this._datadogService,
            cameraId: this.context.camera_id,
            protocol: PLAYERS_REFERENCE.PROTOCOL.HLS,
            sourceUi: PLAYERS_REFERENCE.SOURCE_UI.HLS,
            errorType: data.type || 'unknown',
            rawError: errorDetails,
            errorCode,
            isLive: this.live,
            isFatal: data.fatal || false,
            streamUrl: src,
            context: this.context,
            protocolSpecificFields: {
              error_details: data.details || errorDetails,
              hls_error_type: data.type,
              hls_error_details: data.details,
              hls_fatal: data.fatal,
              hls_buffer_stalled: this.isBufferStalled,
              hls_current_level: this.hls?.currentLevel,
              hls_stream_recovering: this.isStreamRecovering,
            },
          });
        }
        if (data?.fatal) {
          this._emitStreamAttemptEnd('fail', 'manifest');
          this.errorCallback();
          this._lastPosition = this.currentTime;
          this._hlsTryLoad(src);
          this._lastPosition = 0;
        }
        if (
          data?.details === Hls.ErrorDetails.BUFFER_STALLED_ERROR ||
          data?.details === Hls.ErrorDetails.BUFFER_NUDGE_ON_STALL
        ) {
          this._hlsStallBegin();
        }
        switch (data?.type) {
          case Hls.ErrorTypes.NETWORK_ERROR:
            if (data?.details === Hls.ErrorDetails.FRAG_LOAD_TIMEOUT) {
              this._hlsStallBegin();
              if (!this.isStreamRecovering) {
                this._lastPosition = this.currentTime;
              }
              this.hls.loadLevel = this.hls.loadLevel + 1;
              this.isStreamRecovering = true;
              this._hlsTryLoad(src);
            }
        }
        console.error('HLS PLAYER ERROR:', error, data);
      });
      this.hls.on(Hls.Events.MANIFEST_LOADED, (_, data: ManifestLoadedData) => {
        const manifestTotalLoadTime = Math.round(data.stats.loading.end - data.stats.loading.start);
        const manifestTTFBTime = Math.round(data.stats.loading.first - data.stats.loading.start);
        this._emitManifestLoaded(data.stats.total, manifestTotalLoadTime, manifestTTFBTime);
      });
      this.hls.on(Hls.Events.MANIFEST_PARSED, () => {
        if (autoplay) {
          const playPromise = this.videoElement.play();
          playPromise.catch((error) => {
            console.error('HLS PLAYER ERROR:', error);
            return null;
          });
        }
        this.callback();
      });

      this.hls.on(Hls.Events.FRAG_CHANGED, (id, data: FragChangedData) => {
        if (!this._connected) {
          this._connected = true;
          this.callback();
        }
        this._currentFrag = data.frag;
        this._fragChanged$.next();
      });

      this.hls.on(Hls.Events.FRAG_LOADED, () => {
        if (this._lastPosition > 0) {
          this.videoElement.currentTime = this._lastPosition;
          this._lastPosition = 0;
          this.isStreamRecovering = false;
        }

        this._fragLoaded$.next();
      });

      this.hls.on(Hls.Events.FRAG_BUFFERED, (_, data: FragBufferedData) => {
        const downloadTimeMs = data.stats.loading.end - data.stats.loading.start;
        const segmentLengthMs = data.frag.duration * 1000;
        this._downloadAsFractionOfSegLength.addValue(downloadTimeMs / segmentLengthMs);
      });

      this.hls.on(Hls.Events.FRAG_LOADING, () => {
        this._fragLoading$.next();
      });

      this.hls.on(Hls.Events.LEVEL_LOADED, () => {
        this._levelLoaded$.next();
        const targetDurationMs = this._getTargetDurationMs();
        if (targetDurationMs) {
          this._targetDurationMs = targetDurationMs;
          this._updatePeriodicMetricsInterval();
        }
      });

      this.hls.on(Hls.Events.BUFFER_APPENDED, () => {
        this.callback();
        this._hlsStallEnd();
      });
    }
  }

  private _hlsTryLoad(src: string): void {
    this.hls.loadSource(src);
    this.hls.startLoad();
    this.videoElement.currentTime = this._lastPosition;
  }

  private _hlsStallBegin(): void {
    if (!this.isBufferStalled) {
      this._stallCount += 1;
      this._stallStartTime = Date.now();
      this.isBufferStalled = true;
      this._emitStallCount();
    }
  }

  private _hlsStallEnd(): void {
    if (!this.isBufferStalled) {
      return;
    }

    if (this._stallStartTime) {
      const stallDuration = Date.now() - this._stallStartTime;
      this._emitStallDuration(stallDuration);
      this._stallStartTime = null;
    }

    this.isBufferStalled = false;
  }

  private _isFullscreen(): boolean {
    return !!(document.fullscreenElement || this.playerElement.classList.contains('fullscreen'));
  }

  private _startPeriodicMetrics(): void {
    this._stopPeriodicMetrics();
    this._periodicMetricsSubscription = interval(this._periodicMetricsIntervalMs)
      .pipe(takeUntil(this._destroy$))
      .subscribe(() => {
        this._sendPeriodicMetrics();
      });
  }

  private _sendPeriodicMetrics(): void {
    if (this._downloadAsFractionOfSegLength.hasValues()) {
      this._emitSegmentPercent(this._downloadAsFractionOfSegLength.getMean());
    }
    this._downloadAsFractionOfSegLength.clear();
  }

  private _stopPeriodicMetrics(): void {
    if (this._periodicMetricsSubscription) {
      this._periodicMetricsSubscription.unsubscribe();
      this._periodicMetricsSubscription = null;
    }
  }

  private _updatePeriodicMetricsInterval(): void {
    if (!this._targetDurationMs) {
      return;
    }
    const K = Math.max(3, Math.ceil(30_000 / this._targetDurationMs));
    const newInterval = K * this._targetDurationMs;
    if (newInterval !== this._periodicMetricsIntervalMs) {
      this._periodicMetricsIntervalMs = newInterval;
      this._stopPeriodicMetrics();
      this._startPeriodicMetrics();
    }
  }

  private _getTargetDurationMs(): number | null {
    if (!this.hls?.levels || this.hls.levels.length === 0) {
      return null;
    }
    const currentLevel: Level = this.hls.levels[this.hls.currentLevel];
    if (!currentLevel) {
      return null;
    }
    const targetDurationSecs = currentLevel?.details?.targetduration ?? 0;
    if (typeof targetDurationSecs === 'number' && targetDurationSecs > 0) {
      return targetDurationSecs * 1000;
    }
    return null;
  }

  private _initSeekDebounce(): void {
    this._seekSubscription = this._seekStart$
      .pipe(debounceTime(300), takeUntil(this._destroy$))
      .subscribe(() => {
        if (this._firstFrameLoaded) {
          this._isSeeking = true;
          this._seekStartTime = Date.now();
          this._emitStreamAttemptStart('seek');
        }
      });
  }

  private _handleSeekStart(): void {
    if (this._firstFrameLoaded && this._isSeeking && this._seekStartTime) {
      this._emitStreamAttemptEnd('canceled', 'seek');
      this._isSeeking = false;
    }
    this._seekStart$.next();
  }

  private _handleSeekEnd(): void {
    if (!this._isSeeking || !this._seekStartTime) {
      return;
    }

    this._emitStreamAttemptEnd('success', 'seek');

    this._isSeeking = false;
  }

  private _emitResumePlaybackLatency(latencyMs: number): void {
    this._datadogService.addAction('hls_resume_playback_latency', {
      ...this.context,
      timestamp: Date.now(),
      camera_id: this.context.camera_id,
      source_ui: PLAYERS_REFERENCE.SOURCE_UI.HLS,
      latency_ms: latencyMs,
      source: this.context.source,
      camera_status: this.context.camera_status,
      live: this.context.live,
    });
  }

  private _emitManifestLoaded(sizeBytes: number, totalMs: number, ttfbMs: number): void {
    this._datadogService.addAction('hls_manifest_loaded', {
      ...this.context,
      camera_id: this.context.camera_id,
      source_ui: PLAYERS_REFERENCE.SOURCE_UI.HLS,
      manifest_size_bytes: sizeBytes,
      total_ms: totalMs,
      ttfb_ms: ttfbMs,
      source: this.context.source,
      camera_status: this.context.camera_status,
      live: this.context.live,
    });
  }

  private _emitStallCount(): void {
    this._datadogService.addAction('stream_stall_count', {
      ...this.context,
      timestamp: Date.now(),
      camera_id: this.context.camera_id,
      source_ui: PLAYERS_REFERENCE.SOURCE_UI.HLS,
      protocol: PLAYERS_REFERENCE.PROTOCOL.HLS,
      stall_count: this._stallCount,
      playback_rate: this.playbackRate,
      is_fullscreen: this._isFullscreen(),
      source: this.context.source,
      camera_status: this.context.camera_status,
      live: this.context.live,
    });
  }

  private _emitStallDuration(durationMs: number): void {
    this._datadogService.addAction('stream_stall_duration', {
      ...this.context,
      timestamp: Date.now(),
      camera_id: this.context.camera_id,
      source_ui: PLAYERS_REFERENCE.SOURCE_UI.HLS,
      protocol: PLAYERS_REFERENCE.PROTOCOL.HLS,
      stall_duration_ms: durationMs,
      playback_rate: this.playbackRate,
      is_fullscreen: this._isFullscreen(),
      source: this.context.source,
      camera_status: this.context.camera_status,
      live: this.context.live,
    });
  }

  private _emitSegmentPercent(averageDownloadFraction: number): void {
    this._datadogService.addAction('hls_segment_percent', {
      ...this.context,
      timestamp: Date.now(),
      camera_id: this.context.camera_id,
      source_ui: PLAYERS_REFERENCE.SOURCE_UI.HLS,
      average_download_length_fraction: averageDownloadFraction,
      playback_rate: this.playbackRate,
      is_fullscreen: this._isFullscreen(),
      source: this.context.source,
      camera_status: this.context.camera_status,
      live: this.context.live,
    });
  }

  private _emitStreamAttemptStart(playback_method?: 'manifest' | 'seek'): void {
    this._datadogService.addAction('stream_attempt_start', {
      ...this.context,
      camera_id: this.context.camera_id,
      source_ui: PLAYERS_REFERENCE.SOURCE_UI.HLS,
      protocol: PLAYERS_REFERENCE.PROTOCOL.HLS,
      source: this.context.source,
      playback_method,
      camera_status: this.context.camera_status,
      live: this.context.live,
    });
  }

  private _emitStreamAttemptEnd(
    status: 'success' | 'fail' | 'canceled',
    playback_method?: 'manifest' | 'seek',
  ): void {
    const startTime = playback_method === 'seek' ? this._seekStartTime : this._playStartTime;
    if (!startTime) {
      return;
    }
    const latencyMs = Date.now() - startTime;

    this._datadogService.addAction('stream_attempt_end', {
      ...this.context,
      camera_id: this.context.camera_id,
      source_ui: PLAYERS_REFERENCE.SOURCE_UI.HLS,
      protocol: PLAYERS_REFERENCE.PROTOCOL.HLS,
      status,
      latency_ms: latencyMs,
      source: this.context.source,
      playback_method,
      camera_status: this.context.camera_status,
      live: this.context.live,
    });

    if (playback_method === 'seek') {
      this._seekStartTime = null;
    } else {
      this._playStartTime = null;
    }
  }
}
